import type { DockviewReadyEvent } from 'dockview-react';
import { DockviewReact } from 'dockview-react';
import { useCallback, useRef } from 'react';
import { PANEL_COMPONENTS } from '@/panels';
import { syncOpenPanelIds, useDockviewApiStore, useLayoutPersistStore } from '@/stores/layoutStore';
import { useUiStore } from '@/stores/uiStore';
import { buildDefaultLayout } from './defaultLayout';

/**
 * The dockable panel grid. Restores the last-saved arrangement on mount (or
 * builds the out-of-the-box one if there isn't one yet), then keeps
 * `layoutStore`'s persisted copy in sync with every user rearrangement.
 */
export function DockLayout() {
  const setApi = useDockviewApiStore((state) => state.setApi);
  const savedLayout = useLayoutPersistStore((state) => state.savedLayout);
  const setSavedLayout = useLayoutPersistStore((state) => state.setSavedLayout);
  const savedLayoutRef = useRef(savedLayout);
  savedLayoutRef.current = savedLayout;

  const onReady = useCallback(
    (event: DockviewReadyEvent) => {
      const { api } = event;
      setApi(api);

      if (savedLayoutRef.current) {
        try {
          api.fromJSON(savedLayoutRef.current);
        } catch (error) {
          // A layout saved against an older panel set can fail to restore;
          // fall back to the default rather than leaving the shell blank.
          console.warn('Could not restore the saved panel layout; using the default.', error);
          buildDefaultLayout(api);
          useUiStore.getState().notify({
            kind: 'warning',
            title: 'Panel layout was repaired',
            message:
              'The saved layout was incompatible or damaged, so the default workspace was restored. Mission data was not affected.',
          });
        }
      } else {
        buildDefaultLayout(api);
      }

      syncOpenPanelIds(api);
      api.onDidLayoutChange(() => {
        setSavedLayout(api.toJSON());
        syncOpenPanelIds(api);
      });
    },
    [setApi, setSavedLayout],
  );

  return (
    <DockviewReact
      className="dockview-theme-trajectory flex-1"
      components={PANEL_COMPONENTS}
      onReady={onReady}
    />
  );
}
